import { createContext, useCallback, useContext, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Action, Dispatch } from 'redux';
import services from '../../services';
import { getFilePath } from '../../services/media';
import { hideModal } from '../../store/actions/modal';
import { MediaEntities } from '../../types/miscelaneos/media.dto';
import UploadingFilesModal from './components/UploadingFilesModal';

interface FileUploadProgress {
  preview: string;
  fileName: string;
  fileType: string;
  originalName: string;
  progress: number;
  remainingTime: string;
}

interface ContexInitialValuesDto {
  uploadFiles: (files: { [key: string]: File[] }, entity: MediaEntities, extraData?: any) => Promise<{}>
  fileUploadProgress: {
    originalName: string
    fileType: string
    preview: string
    progress: number
  }[]
  resetFileUploadProgress: () => void
  buildFilePayload: (values: any, paths: string[]) => {
    [key: string]: File[]
  }
  assignUploadedFilesToPayload: (
    payload: any,
    uploadedFiles: Record<string, File[]>,
    paths: string[]
  ) => any
  cleanFilesFromData: (
    data: any,
    paths: string[]
  ) => any,
}

const contexInitialValues: ContexInitialValuesDto = {
  uploadFiles: async () => Promise.resolve({}),
  fileUploadProgress: [],
  resetFileUploadProgress: () => { },
  buildFilePayload: () => { return {} },
  assignUploadedFilesToPayload: () => { },
  cleanFilesFromData: () => { },
};


// Define the context for file upload progress
const FileUploadProgressContext = createContext<ContexInitialValuesDto | undefined>(contexInitialValues)

/**
 * Hook to use the file upload progress context
 * @returns {Object} - The file upload context.
 * @throws Will throw an error if used outside of FileUploadProgressProvider.
 */
export const useFileUploadProgress = () => {
  const context = useContext(FileUploadProgressContext);
  if (!context) {
    throw new Error('useFileUploadProgress must be used within a FileUploadProgressProvider');
  }
  return context;
};

interface FileUploadProgressProviderProps {
  children,
  currentUser,
  currentCompany,
  visibleModal
}

const FileUploadProgressProvider = ({ children, currentUser, currentCompany, visibleModal }: FileUploadProgressProviderProps) => {
  const dispatch = useDispatch<Dispatch<Action>>();

  // State to hold file upload progress
  const [fileUploadProgress, setFileUploadProgress] = useState<FileUploadProgress[]>([]);
  const [uploadSpeed, setUploadSpeed] = useState<number>(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [startBytesLoaded, setStartBytesLoaded] = useState<number>(0);

  /**
   * Function to upload files
   * @param {Object} files - An object where keys are file types and values are arrays of files.
   * @param {string} entity - The ID of the user.
   * @param {Object} extraData - Is optional, used to create file path
   * @returns {Object} uploadedFiles - An object containing information about successfully uploaded files.
   */
  const uploadFiles = useCallback(async (files: { [key: string]: File[] }, entity: MediaEntities, extraData: { idResponsible?: string } = {}) => {
    const timestamp = new Date().getTime();
    const uploadedFiles = {};
    let fileIndex = 0;

    for (const [fieldName, fileList] of Object.entries(files)) {
      fileIndex++
      uploadedFiles[fieldName] = uploadedFiles[fieldName] || [];

      const fileListFiltered = fileList.filter(file => file instanceof File);
      const nonFileList = fileList.filter(file => !(file instanceof File));

      nonFileList.forEach(file => {
        uploadedFiles[fieldName].push(file);
      });

      const uploadPromises = fileListFiltered.map((file: File & { url?: string; preview?: string }, i): Promise<{ response?: any; error?: any }> => {
        const fileExt = file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 2);
        const key = `${timestamp}-${fileIndex}-${i}`;

        return new Promise((resolve, reject) => {
          services.media.saveFile({
            file,
            filePath: getFilePath({
              entity,
              fieldName,
              idCompany: currentCompany?._id,
              idUser: currentUser?._id,
              idResponsible: currentUser?.responsible?._id,
              ...extraData
            }),
            fileName: `${key}.${fileExt}`,
            fileType: file?.type,
            onUploadProgress: (uploadProgress) => {
              updateFileUploadProgress({
                preview: file.url || file.preview,
                fileName: `${key}.${fileExt}`,
                fileSize: file.size,
                fileType: file.type,
                originalName: file.name,
                progress: uploadProgress
              });
            },
            onAbort: () => dispatch(hideModal())
          })
            .then((response: { response?: any, error?: any }) => resolve(response))
            .catch(error => reject(error));
        });
      });

      try {
        const uploadResponses = await Promise.all(uploadPromises);

        const allUploadsSuccessful = uploadResponses.every((response) => 'response' in response);

        if (allUploadsSuccessful) {
          const uploadedFilesForType = fileListFiltered.map((file, i) => {
            const fileExt = file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 2);
            const key = `${timestamp}-${fileIndex}-${i}`;

            return {
              field: fieldName,
              key,
              fileName: `${key}.${fileExt}`,
              originalName: file.name,
              extension: fileExt,
              mimeType: file.type,
              size: file.size,
              path: getFilePath({
                entity,
                fieldName,
                idCompany: currentCompany?._id,
                idUser: currentUser?._id,
                idResponsible: currentUser?.responsible?._id,
                ...extraData
              }),
            };
          });

          uploadedFiles[fieldName].push(...uploadedFilesForType);
        } else {
          console.error(`One or more file uploads for ${fieldName} failed.`);
        }
      } catch (error) {
        console.error(`Error during file uploads for ${fieldName}:`, error);
      }
    }

    return uploadedFiles;
  }, [currentUser, currentCompany]);

  /**
   * Function to format time in HH:MM:SS
   * @param {number} seconds - The time in seconds.
   * @returns {string} - A string formatted as HH:MM:SS.
   */
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const pad = (num) => (num < 10 ? "0" + num : num);

    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  };

  /**
   * Function to calculate upload speed
   * @param {number} bytesLoaded - The number of bytes loaded.
   * @returns {number} - The upload speed in bytes per second.
   */
  const calculateUploadSpeed = (bytesLoaded) => {
    const currentTime = new Date().getTime();
    const elapsedTimeInSeconds = (currentTime - startTime) / 1000;
    const bytesUploaded = bytesLoaded - (startBytesLoaded || 0);
    const uploadSpeed = bytesUploaded / elapsedTimeInSeconds;

    return uploadSpeed;
  };

  /**
   * Function to handle upload start
   */
  const onUploadStart = () => {
    setStartTime(new Date().getTime());
    setStartBytesLoaded(0);
    setUploadSpeed(0);
  };

  /**
   * Function to handle upload progress
   * @param {Event} event - The progress event containing the loaded bytes.
   */
  const onUploadProgress = (event) => {
    const bytesLoaded = event.loaded;

    if (!startTime) {
      setStartTime(new Date().getTime());
      setStartBytesLoaded(bytesLoaded);
      setUploadSpeed(0);
      return;
    }

    const currentUploadSpeed = calculateUploadSpeed(bytesLoaded);
    setUploadSpeed(currentUploadSpeed);
  };

  /**
   * Function to update file upload progress
   * @param {Object} params - The parameters for updating the file upload progress.
   * @param {string} params.fileName - The name of the file.
   * @param {string} params.fileType - The type of the file.
   * @param {string} params.originalName - The original name of the file.
   * @param {number} params.progress - The upload progress.
   * @param {number} params.fileSize - The size of the file.
   * @param {string} [params.preview] - An optional preview of the file.
   */
  const updateFileUploadProgress = ({ fileName, fileType, originalName, progress, fileSize, preview }) => {
    setFileUploadProgress((prevProgress) => {
      const updatedProgress = [...prevProgress];
      const fileIndex = updatedProgress.findIndex((file) => file.fileName === fileName);

      if (fileIndex !== -1) {
        const remainingBytes = fileSize - progress;
        const remainingTimeInSeconds = remainingBytes / (uploadSpeed || 1);
        const remainingTimeFormatted = formatTime(remainingTimeInSeconds);

        updatedProgress[fileIndex].progress = progress;
        updatedProgress[fileIndex].remainingTime = remainingTimeFormatted;
      } else {
        updatedProgress.push({ preview, fileName, fileType, originalName, progress, remainingTime: '' });
      }

      return updatedProgress;
    });
  };

  /**
   * Function to reset file upload progress
   */
  const resetFileUploadProgress = () => {
    setFileUploadProgress([]);
  };

  const buildFilePayload = (values: any, paths: string[]) => {
    return paths.reduce((acc, path) => {
      const file = path.split('.').reduce((obj, key) => obj?.[key], values);

      if (file instanceof File) {
        acc[path] = [file];
      }
      return acc;
    }, {} as Record<string, File[]>);
  };

  const assignUploadedFilesToPayload = (
    payload: any,
    uploadedFiles: Record<string, File[]>,
    paths: string[]
  ) => {
    paths.forEach(path => {
      const file = uploadedFiles[path];

      if (file && file.length > 0) {
        const keys = path.split('.');
        let current = payload;

        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];

          const indexMatch = key.match(/^\d+$/);
          if (indexMatch) {
            const index = parseInt(key, 10);
            if (!Array.isArray(current)) {
              current = [];
            }
            if (!current[index]) {
              current[index] = {};
            }
            current = current[index];
          } else {
            if (!current[key]) {
              current[key] = {};
            }
            current = current[key];
          }
        }

        const lastKey = keys[keys.length - 1];
        current[lastKey] = file[0];
      } else {
        console.warn(`No se encontró archivo en uploadedFiles para path: ${path}`);
      }
    });

    return payload;
  };

  const cleanFilesFromData = (data, filePaths) => {
    filePaths.forEach(path => {
      const keys = path.split('.'); // Split the dot notation into an array of keys
      let obj = data; // Start from the root of the data object

      // Traverse through the object until reaching the second to last key
      for (let i = 0; i < keys.length - 1; i++) {
        if (obj === null || obj === undefined || !obj[keys[i]]) {
          return; // If obj is null/undefined or if the key doesn't exist, exit
        }
        obj = obj[keys[i]]; // Traverse deeper
      }

      // Set the file path to undefined if obj is not null/undefined
      if (obj && obj[keys[keys.length - 1]] !== undefined) {
        obj[keys[keys.length - 1]] = undefined;
      }
    });

    return data; // Return the modified object
  };

  const areFiles = (array) => {
    let thereAreFiles = false
    if (Array.isArray(array)) {
      for (let i = 0; i < array.length; i++) {
        const file = array[i];
        if (file && typeof file === 'object' && 'name' in file && 'size' in file && 'type' in file) {
          thereAreFiles = true
          break
        }
      }
    }

    return thereAreFiles
  }

  return (
    <FileUploadProgressContext.Provider
      value={{
        uploadFiles,
        fileUploadProgress,
        resetFileUploadProgress,
        buildFilePayload,
        assignUploadedFilesToPayload,
        cleanFilesFromData
      }}
    >
      {children}
      <UploadingFilesModal />
    </FileUploadProgressContext.Provider>
  );
};

const state = ({ companyStore, userStore, modalStore }) => {
  const { data: currentUser } = userStore.currentUser
  const { data: currentCompany } = companyStore.selected
  const { visibleModal } = modalStore

  return {
    currentUser,
    currentCompany,
    visibleModal
  };
};

export default connect(state)(FileUploadProgressProvider); 